import { motion } from "framer-motion";
import { Star, ExternalLink, MessageSquare } from "lucide-react";
import { CONTACT } from "@/data/site";

export default function Testimonials() {
  // Verified customer reviews have been intentionally held back until the
  // business supplies real quotes. Rather than show placeholder names/quotes,
  // we point visitors to the live Thumbtack profile.
  return (
    <section id="reviews" data-testid="testimonials-section" className="rs-section bg-[#F8FAFC]">
      <div className="rs-container">
        <div className="text-center max-w-2xl mx-auto">
          <p className="rs-overline mb-4">Verified Reviews</p>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            Read real reviews from real customers.
          </h2>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            Our customer reviews live on Thumbtack where every rating is verified.
            Check them before you book — and if you&apos;ve hired us, we&apos;d love your honest feedback.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto"
        >
          <a
            href={CONTACT.thumbtackUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="thumbtack-reviews-cta"
            className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 p-7 hover:border-[#F97316]/50 hover:shadow-[0_16px_40px_-16px_rgba(10,25,47,0.18)] transition-all"
          >
            <div className="flex items-center gap-2 text-[#F97316]">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-5 w-5 fill-current" />
              ))}
            </div>
            <h3 className="mt-4 font-display font-semibold text-xl text-[#0A192F] leading-tight">
              See all reviews on Thumbtack
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Independently verified by Thumbtack. Browse recent jobs, photos, and ratings.
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#F97316] group-hover:gap-2.5 transition-all">
              Open Thumbtack profile
              <ExternalLink className="h-4 w-4" />
            </div>
          </a>

          <a
            href={CONTACT.thumbtackUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="leave-review-cta"
            className="group relative overflow-hidden rounded-2xl bg-[#0A192F] text-white p-7 hover:shadow-[0_16px_40px_-16px_rgba(249,115,22,0.35)] transition-all border border-[#0A192F]"
          >
            <div className="h-11 w-11 rounded-xl bg-[#F97316]/15 border border-[#F97316]/30 grid place-items-center">
              <MessageSquare className="h-5 w-5 text-[#F97316]" />
            </div>
            <h3 className="mt-4 font-display font-semibold text-xl leading-tight">
              Hired us before? Leave a review.
            </h3>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">
              Honest reviews help neighbors find a trades pro they can trust.
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#F97316] group-hover:gap-2.5 transition-all">
              Share your experience
              <ExternalLink className="h-4 w-4" />
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
